/**
 * TanStack Query hook for `GET /api/cro/metrics/:metricId`.
 *
 * Used by the 9 big-number CRO tiles (CRO-01/02/03/04/05/08/09/10/11).
 * Each tile call fires in parallel on CroScorecardPage mount; TanStack Query
 * handles dedup if the same metricId is requested twice (won't happen in practice).
 *
 * Config:
 *   - queryKey: ['cro', 'metric', metricId]
 *   - staleTime: 60s — cache-first from agg_cro_scorecard (10-min cron)
 *   - retry: 2 — fetch-level retries on blips (SF + cache error paths are
 *     filtered by the global queryClient retry in App.tsx)
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  apiFetch,
  type ApiClientError,
  type ApiServerError,
  type CacheNotReadyError,
  type SalesforceUnavailableError,
  type UnauthenticatedError,
} from '../lib/api-client.js';

import type { CroMetricResponse } from '@saledash/shared';

export type MetricError =
  | SalesforceUnavailableError
  | CacheNotReadyError
  | ApiServerError
  | ApiClientError
  | UnauthenticatedError;

export function useCroScorecard(
  metricId: string,
): UseQueryResult<CroMetricResponse, MetricError> {
  return useQuery<CroMetricResponse, MetricError>({
    queryKey: ['cro', 'metric', metricId],
    queryFn: ({ signal }) =>
      apiFetch<CroMetricResponse>(`/api/cro/metrics/${metricId}`, { signal }),
    staleTime: 60_000,
    retry: 2,
  });
}
